import React from 'react';
import { GlobalMicroButton } from './GlobalMicroButton';
import styles from './SmallServiceCard.module.css';
import { useTheme } from '../context/ThemeProvider';

interface SmallServiceCardProps {
  id: string;
  title: string;
  description: string;
  fileTypeIcon: string;
  serviceIcon: string;
  buttonIcon?: string;
  buttonText: string;
  buttonOnClick: () => void;
  isDone?: boolean;
  isLoading?: boolean;
  statusText?: string;
  statusColor?: string;
  brandColor?: string;
  className?: string;
  disabled?: boolean;
}

const SmallServiceCard: React.FC<SmallServiceCardProps> = ({
  id,
  title,
  description,
  fileTypeIcon,
  serviceIcon,
  buttonIcon,
  buttonText,
  buttonOnClick,
  isDone = false,
  isLoading = false,
  statusText,
  statusColor,
  brandColor,
  className,
  disabled = false
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // Resolve CSS variable to hex value for opacity support
  const getBrandColorWithOpacity = () => {
    if (!brandColor) return '';
    
    // If it's a CSS variable, resolve it to hex based on theme
    if (brandColor.startsWith('var(--')) {
      const varName = brandColor.match(/var\(--([^)]+)\)/)?.[1];
      if (varName === 'wild-watermelon') {
        return isDarkMode ? '#C9416250' : '#C5303050';
      } else if (varName === 'shamrock-green') {
        return isDarkMode ? '#06878150' : '#29ae9a50';
      }
      // Fallback: try to get computed value (may not work in all contexts)
      return brandColor;
    }
    
    // If it's already a hex value, append opacity
    return `${brandColor}50`;
  };

  return (
    <div className={`${styles.serviceCard} ${className || ''}`} id={id}>

      {/* Brand Gradient Overlay */}
      {brandColor && (
        <div
          className={styles.brandGradient}
          style={{
            background: `radial-gradient(circle at top right, ${getBrandColorWithOpacity()}, transparent 95%)`
          }}
        />
      )}

      <div className={styles.serviceContent}>
        <div className={styles.serviceTitle}>

          {/* File type icon */}
          {fileTypeIcon && (
            <img
              src={fileTypeIcon}
              alt={title}
              width={20}
              height={20}
            />
          )}

          {/* Title */}
          <h3>{title}</h3>

        </div>

        {/* Description */}
        <p className={styles.serviceDescription}>{description}</p>

        {/* Button or Status */}
        <div className={styles.serviceActions}>
          {isDone ? (
            <div
              className={styles.statusText}
              style={{ color: statusColor }}
            >
              <img src="/assets/icons/checkmark.circle.fill.green.svg" className={isDarkMode ? undefined : "darkenGreenIcon"} alt="Status" width={12} height={12} />
              {statusText || 'Done'}
            </div>
          ) : (
            <GlobalMicroButton
              onClick={buttonOnClick}
              disabled={isLoading || disabled}
              isLoading={isLoading}
              icon={buttonIcon && buttonIcon.trim() !== '' ? <img src={buttonIcon} alt="Action" width={16} height={16} /> : undefined}
            >
              {buttonText}
            </GlobalMicroButton>
          )}
        </div>
      </div>

      {/* Service Icon */}
      {serviceIcon && serviceIcon.trim() !== '' && (
        <div className={styles.serviceIcon}>
          <img
            src={serviceIcon}
            alt={title}
            width={24}
            height={24}
          />
        </div>
      )}
    </div>
  );
};

export default SmallServiceCard;

